import {useCallback, useEffect, useRef, useState} from "react";
import {NavigationType} from "react-router-dom";
import {bffApi} from "../chat/bffApi";
import {metricUuid} from "../chat/metrics";
import {
    markIslandCounted,
    startIslandVisit,
    takeTopicEntry,
    type TopicEntrySource,
} from "./topicEntryNote";

type Options = {
    /** The topic being shown; undefined while unknown. */
    topicId: string | undefined;
    /** True once the topic is actually shown to a user with access. */
    shown: boolean;
    surface: "page" | "island";
    segmentId?: string;
    sectionId?: string;
    topicType?: string;
    /** Used when no click left a note: island opens always come from a citation. */
    fallbackSource: TopicEntrySource;
    /** PDFs: the open also waits for the document, reported through the returned callback. */
    waitForDocument?: boolean;
};

type Visit = {
    id: number;
    source: TopicEntrySource;
    alreadyCounted: boolean;
    counted: boolean;
};

/**
 * Without a click, a pop is a page load, a reload or back/forward, so the
 * channel is unknown; a push or replace is an in-app link.
 */
export const pageFallbackSource = (navigationType: NavigationType): TopicEntrySource =>
    navigationType === NavigationType.Pop ? "direct_unknown" : "internal_link";

/**
 * Records `kai.topic.opened` once per arrival on a topic: A → B → A counts A
 * twice, also when B never loads, while segment and hash changes within A do
 * not count again. The entry source is read on arrival, so a slow load keeps
 * it. Returns the callback that reports the document load for `waitForDocument`.
 */
export function useTopicOpenAnalytics({
    topicId,
    shown,
    surface,
    segmentId,
    sectionId,
    topicType,
    fallbackSource,
    waitForDocument = false,
}: Options) {
    const [trackOpened] = bffApi.endpoints.trackOpened.useMutation();
    const key = metricUuid(topicId);

    // A visit starts whenever the topic changes. Its id is known while
    // rendering, so the document callback is bound to the visit itself:
    // a late load from an earlier visit, even to the same topic, never counts.
    const [arrival, setArrival] = useState({key, id: 1});
    if (arrival.key !== key) setArrival({key, id: arrival.id + 1});
    const visitId = arrival.id;

    const visit = useRef<Visit | null>(null);
    const [loadedVisit, setLoadedVisit] = useState<number | null>(null);

    useEffect(() => {
        if (!key) {
            visit.current = null;
            return;
        }
        if (visit.current?.id !== visitId) {
            const note = takeTopicEntry(key);
            visit.current = {
                id: visitId,
                source: note?.source ?? fallbackSource,
                alreadyCounted: note?.alreadyCounted ?? false,
                counted: false,
            };
            if (surface === "island") startIslandVisit(key);
        }

        const current = visit.current;
        const loaded = !waitForDocument || loadedVisit === current.id;
        if (!shown || !loaded || current.counted) return;
        current.counted = true;
        if (current.alreadyCounted) return;
        if (surface === "island") markIslandCounted(key);

        void trackOpened({
            entity: "topic",
            key,
            entrySource: current.source,
            surface,
            segmentId: metricUuid(segmentId),
            sectionId: sectionId || undefined,
            topicType,
        });
    }, [key, visitId, shown, surface, segmentId, sectionId, topicType, fallbackSource, waitForDocument, loadedVisit, trackOpened]);

    return useCallback(() => setLoadedVisit(visitId), [visitId]);
}
